import { useMemo, useState } from 'react';
import { Tabs, TabsItem } from '@hm-group/bads-react';
import { ChannelType } from '../../models';
import { SelectionBar } from '../SelectionBar/SelectionBar';
import { DepartmentRegion } from '../DepartmentRegion/DepartmentRegion';
import { SectionRegion } from '../SectionRegion/SectionRegion';
import { useSelectionBarLogic } from '../../hooks/useSelectionBarLogic';
import { useAppContext } from '../../context/AppContext';
import { ScaContextProvider } from '../../context/ScaContext';
import { MarketContextProvider } from '../../context/MarketContext';
import { KpiContextProvider } from '../../context/KpiContext';
import styles from './PlanningView.module.css';

interface PlanningViewProps {
    onSelectedViewChange?: (view: 'department' | 'section') => void;
}

export function PlanningView({
    onSelectedViewChange,
}: Readonly<PlanningViewProps>) {
    const { searchBarData } = useAppContext();
    const [selectedTab, setSelectedTab] = useState('0');

    const {
        isDepartmentSelected,
        isSectionSelected,
        selectionBarState,
        markets,
        visibleStoreMarkets,
        visibleOnlineMarkets,
        storeConnectedMarketCodes,
        onlineConnectedMarketCodes,
        storeDisconnectedMarketCodes,
        onlineDisconnectedMarketCodes,
        storeMainMarketCode,
        onlineMainMarketCode,
        kpiSelection,
        isIncludeExcitementToggleChecked,
        isShowGroundArticlesToggleChecked,
        departmentMetadata,
        scaModalConfig,
        marketModalConfig,
        pendingScaAction,
        clearScaAction,
        pendingMarketAction,
        clearMarketAction,
        handleMarketLinkCompleted,
        handleSelectedMarketsChanged,
        handleCoreExtendedChange,
        handleExcitementChange,
        handleIncludeExcitementToggleChange,
        handleShowGroundArticlesToggleChange,
        handleViewChange,
        handleTabChange,
    } = useSelectionBarLogic();

    const handleViewChangeInternal = (view: 'department' | 'section') => {
        handleViewChange(view);
        onSelectedViewChange?.(view);
    };

    const handleTabChangeInternal = (value: string) => {
        setSelectedTab(value);
        handleTabChange(Number(value));
    };

    const tabIndex = Number(selectedTab);
    const selectedView = selectionBarState.selectedView;

    const storeMarkets = useMemo(
        () => markets.filter((m) => m.channelIntegrationKey === ChannelType.Store),
        [markets]
    );
    const onlineMarkets = useMemo(
        () => markets.filter((m) => m.channelIntegrationKey === ChannelType.Online),
        [markets]
    );

    return (
        <div>
            <SelectionBar
                searchBarData={searchBarData}
                isDepartmentSelected={isDepartmentSelected}
                isSectionSelected={isSectionSelected}
                state={selectionBarState}
                scaModalConfig={scaModalConfig}
                marketModalConfig={marketModalConfig}
                onCoreExtendedChange={handleCoreExtendedChange}
                onExcitementChange={handleExcitementChange}
                onIncludeExcitementToggleChange={handleIncludeExcitementToggleChange}
                onShowGroundArticlesToggleChange={handleShowGroundArticlesToggleChange}
                onViewChange={handleViewChangeInternal}
            />

            {(isDepartmentSelected || isSectionSelected) && (
                <div className={styles.tabWrapper}>
                    <Tabs
                        value={selectedTab}
                        onChange={handleTabChangeInternal}
                        direction="horizontal"
                        testId="workspace-tabs"
                    >
                        <TabsItem value="0" label="Store" />
                        <TabsItem value="1" label="Online" />
                    </Tabs>
                </div>)
            }

            <KpiContextProvider value={{
                kpiSelection,
                isIncludeExcitementToggleChecked,
                isShowGroundArticlesToggleChecked,
            }}>
            <ScaContextProvider
                pendingScaAction={pendingScaAction}
                clearScaAction={clearScaAction}
            >
            <MarketContextProvider value={{
                pendingMarketAction,
                clearMarketAction,
                onMarketLinkCompleted: handleMarketLinkCompleted,
                onSelectedMarketsChanged: handleSelectedMarketsChanged,
                connectedMarketCodes: tabIndex === 0 ? storeConnectedMarketCodes : onlineConnectedMarketCodes,
                disconnectedMarketCodes: tabIndex === 0 ? storeDisconnectedMarketCodes : onlineDisconnectedMarketCodes,
                mainMarketCode: tabIndex === 0 ? storeMainMarketCode : onlineMainMarketCode,
                departmentMetadata,
            }}>

            {/* Department view – Store tab */}
            {(isDepartmentSelected || isSectionSelected) && selectedView === 'department' && tabIndex === 0 && (
                <DepartmentRegion
                    channelType={ChannelType.Store}
                    allMarkets={storeMarkets}
                    visibleMarkets={visibleStoreMarkets}
                />
            )}

            {/* Department view – Online tab */}
            {(isDepartmentSelected || isSectionSelected) && selectedView === 'department' && tabIndex === 1 && (
                <DepartmentRegion
                    channelType={ChannelType.Online}
                    allMarkets={onlineMarkets}
                    visibleMarkets={visibleOnlineMarkets}
                />
            )}

            {/* Section view – Store tab */}
            {(isDepartmentSelected || isSectionSelected) && selectedView === 'section' && tabIndex === 0 && (
                <SectionRegion
                    channelType={ChannelType.Store}
                    selectedMarkets={markets}
                />
            )}

            {/* Section view – Online tab */}
            {(isDepartmentSelected || isSectionSelected) && selectedView === 'section' && tabIndex === 1 && (
                <SectionRegion
                    channelType={ChannelType.Online}
                    selectedMarkets={markets}
                />
            )}

            </MarketContextProvider>
            </ScaContextProvider>
            </KpiContextProvider>
        </div>
    );
}
